import { Hidden } from '@mui/material'
import cn from 'classnames'
import moment from 'moment'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { memo, useEffect, useState } from 'react'
import icGraveyard from 'src/assets/ic-graveyard.png'
import { ClaimYourListing } from 'src/components/ClaimYourListing'
import { EditListingButton } from 'src/components/EditListingButton/EditListingButton'
import { CallToActionButton } from 'src/components/global/CallToActionButton/CallToActionButton'
import CompareButton from 'src/components/global/CompareButton'
import { PortfolioPickerOption } from 'src/components/global/CreateNewPortfolioModal'
import { FollowButton } from 'src/components/global/FollowButton'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import VendorLogo from 'src/components/global/VendorLogo/VendorLogo'
import { ProductPageTab } from 'src/components/Tabs/ProductPageTab'
import { THEME } from 'src/config/config'
import {
  IAlspSolutionDataFragment,
  IConsultancySolutionDataFragment,
  IEnum_Solution_Type,
  IProductSolutionDataFragment,
  useSolutionVendorBySlugLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { useSolutionDetailFollow } from 'src/hooks/useSolutionDetailFollow'
import {
  CASE_TAB,
  //CASE_TAB,
  DETAILS_TAB,
  PRICING_TAB,
  PRODUCT_BRIEFING_TAB,
  RESOURCE_TAB,
  VENDOR_ANALYSIS_TAB,
  VIDEOS_TAB,
} from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'
import { isAlspSolution, isConsultancySolution, isProductSolution } from 'src/utils/SolutionDataFragment'

import { SolutionTitle } from '../SolutionTitle/SolutionTitle'

interface SolutionEnhancedHeaderProps {
  className?: string
  solution: IAlspSolutionDataFragment | IConsultancySolutionDataFragment | IProductSolutionDataFragment
  tabIdx: number
  showVendorAnalysis: boolean
  showProductBriefing: boolean
  vendorUsers?: string[]
  onSelectTab: (tabIdx: number) => void
}

/**
 * Primary UI component for user interaction
 */
export const SolutionEnhancedHeader: React.FC<SolutionEnhancedHeaderProps> = memo(
  ({ className, solution, tabIdx, showVendorAnalysis, showProductBriefing, vendorUsers = [], onSelectTab }) => {
    const solutionObject: any = solution
    const router = useRouter()
    const { query } = router
    const { session, status, isVendor } = useAuthenticated()
    const [loginUserId, setLoginUserId] = useState('')
    const [solutionVendorUsers, setSolutionVendorUsers] = useState<string[]>(vendorUsers)

    const enhancedListingData = isAlspSolution(solution)
      ? solution?.alspData?.enhancedListingData
      : isConsultancySolution(solution)
        ? solution?.consultantData?.enhancedListingData
        : isProductSolution(solution)
          ? solution?.productData?.enhancedListingData
          : null
    const callToAction = enhancedListingData ? enhancedListingData.callToAction : null
    let name = isAlspSolution(solution)
      ? solution?.alspData?.name
      : isConsultancySolution(solution)
        ? solution?.consultantData?.name
        : isProductSolution(solution)
          ? solution?.productData?.name
          : null
    let vendorAllUsers = []
    const [loadsolutionData, { data: solutionData }] = useSolutionVendorBySlugLazyQuery()
    useEffect(() => {
      if (!solutionData) {
        loadsolutionData({
          variables: {
            slug: solution?.slug || '',
          },
          fetchPolicy: 'no-cache',
        })
      }
    }, [solutionData, loadsolutionData, solution?.slug])

    useEffect(() => {
      if (solutionData && solutionData?.solutions && solutionData?.solutions?.length > 0) {
        vendorAllUsers = solutionData?.solutions[0]?.vendor?.users?.map((user: any) => {
          return user?.external_id || null
        }) as string[]
        setSolutionVendorUsers(vendorAllUsers)
      }
    }, [solutionData])
    useEffect(() => {
      if (
        session &&
        status === 'authenticated' &&
        session?.user?.idToken != '' &&
        session?.user?.id != '' &&
        loginUserId == ''
      ) {
        setLoginUserId(session?.user?.id)
      }
    }, [session])

    const buyerData: any = solution

    if (buyerData?.consolidationData?.buyer && buyerData?.consolidationData?.date) {
      name = buyerData?.consolidationData?.buyer
    }

    const displayName = solution?.displayName || ''
    const logo = useDefaultLogo(solution?.logo?.url)
    const caseStudies = isAlspSolution(solution)
      ? solution?.alspData?.enhancedListingData?.caseStudies
      : isConsultancySolution(solution)
        ? solution?.consultantData?.enhancedListingData?.caseStudies
        : null
    let isGraveyard = false
    let publishedDate = ''
    let iltaSponsorshipLogo = ''
    let acquiredBy = false

    if (isProductSolution(solution)) {
      if (solution?.graveyardData) {
        const date = solution?.graveyardData?.date
        publishedDate = date ? moment(date).format('MMM DD, YYYY') : ''
        isGraveyard = true
      } else if (solution?.consolidationData) {
        const date = solution?.consolidationData?.date
        publishedDate = date ? moment(date).format('MMM DD, YYYY') : ''
        publishedDate = `Acquired by ${name} on ${publishedDate}`
        if (buyerData?.consolidationData?.buyer && buyerData?.consolidationData?.date) acquiredBy = true
      }
    }

    if ((solution?.iltaSponsorships || []).length > 0) {
      iltaSponsorshipLogo = solution?.iltaSponsorships?.[0]?.logo?.url || ''
    }

    const solutionType = solution?.type || IEnum_Solution_Type.Product
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const solutionAttribute: any = solution
    const productType = solutionAttribute ? solutionAttribute?.productType?.toLowerCase() : ''

    const {
      itemFollowState,
      followingType,
      sharedPortfolioSolutionsForFollow,
      hasLoadedFollowSearch,
      portfolioPickerOptions,
      refreshSharedPortfolios,
    } = useSolutionDetailFollow(solution.documentId, router.isReady)

    const ActionButtons = (
      <>
        {callToAction?.url && (
          <CallToActionButton
            label={callToAction.label}
            url={callToAction.url}
            displayName={displayName}
            solutionId={solution.documentId!}
            solutionType={solutionType}
          />
        )}
        <ThemeOnly theme="legaltech">
          <div className="flex">
            {productType != 'graveyard' &&
              (hasLoadedFollowSearch ? (
                <FollowButton
                  className="mr-2"
                  solutionType={solutionType}
                  id={solution.documentId || ''}
                  displayName={displayName}
                  isEnhanced
                  follow={itemFollowState}
                  isFromVendorList={true}
                  followTypeName={followingType || ''}
                  sharedPortfolioId={undefined}
                  portfolioPickerOptions={portfolioPickerOptions}
                  sharedPortfolioSolutions={sharedPortfolioSolutionsForFollow}
                  styleParameters={'top-[42px]'}
                  onRefresh={refreshSharedPortfolios}
                />
              ) : (
                <FollowButton
                  className="mr-2"
                  solutionType={solutionType}
                  id={solution.documentId || ''}
                  displayName={displayName}
                  isEnhanced
                  follow={false}
                  isFromVendorList={true}
                  followTypeName={'Follow'}
                  sharedPortfolioId={undefined}
                  portfolioPickerOptions={[] as PortfolioPickerOption[]}
                  sharedPortfolioSolutions={[]}
                  styleParameters={'top-[42px]'}
                  onRefresh={refreshSharedPortfolios}
                />
              ))}
            <div className="flex box-border h-[42px] w-[112px] border-solid border-[1px] border-mist rounded-[25px] bg-white items-center justify-center">
              <CompareButton className="items-center" slug={solution?.slug || ''} solutionType={solutionType} />
            </div>
          </div>
        </ThemeOnly>
      </>
    )
    const randomNum = solution?.slug ? solution.slug : Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    const vendorResourcesTL =
      THEME === 'ilta'
        ? solution?.vendorResources?.filter((resource) => {
            if (!resource) return false

            // For Content type resources
            if (resource.type === 'Content') {
              // Exclude if contentType is "Vendor_Analysis" OR documentType is "Product_Briefing"
              const contentAttributes = resource.content
              const publishedAt = contentAttributes?.publishedAt ?? null
              if (resource.type === 'Content' && publishedAt !== null) {
                return true
              }
            }

            // For External type resources
            if (resource.type === 'External') {
              return true
            }

            return false
          }) || []
        : solution?.vendorResources?.filter((resource) => {
            if (!resource) return false

            // For Content type resources
            if (resource.type === 'Content') {
              const contentType = resource.content?.contentType
              const documentType: string | undefined | null = resource.content?.documentType

              // Exclude if contentType is "Vendor_Analysis" OR documentType is "Product_Briefing"
              const contentAttributes = resource.content
              const publishedAt = contentAttributes?.publishedAt ?? null
              if (
                contentType === 'Vendor_Analysis' ||
                documentType === 'Product_Briefing' ||
                (resource.type === 'Content' && publishedAt === null)
              ) {
                return false
              }
            }

            // For External type resources
            if (resource.type === 'External') {
              const documentType: string | undefined | null = resource.external?.documentType

              // Exclude if documentType is "Product_Briefing"
              if (documentType && documentType === 'Product_Briefing') {
                return false
              }
            }

            return true
          }) || []

    const showThoughtLeadershipTab = vendorResourcesTL.length > 0 ? true : false

    const showClaimYourListing =
      solutionObject?.productType !== 'graveyard' || solutionObject?.type === IEnum_Solution_Type.Product

    let enableVendorAnalysisTab = false

    if (showVendorAnalysis) {
      if (status === 'unauthenticated' && !session?.user?.id) {
        enableVendorAnalysisTab = true
      } else if (status === 'authenticated' && session?.user?.id && !isVendor) {
        enableVendorAnalysisTab = true
      } else if (
        status === 'authenticated' &&
        session?.user?.id &&
        isVendor &&
        solutionVendorUsers?.length > 0 &&
        solutionVendorUsers?.includes(session?.user?.id as string)
      ) {
        enableVendorAnalysisTab = true
      }
    }

    return (
      <div
        key={randomNum + 'SolutionEnhancedHeader'}
        className={cn(className, 'flex flex-row justify-center bg-clouds')}
      >
        <div className="flex flex-row justify-center w-full">
          <div className="flex flex-col !max-w-[1100px] w-full mt-[0.9375rem] px-5">
            <div className="flex items-start">
              <div className="flex-1 flex flex-col md:flex-row items-center md:items-start sm:grid-cols-2 mt-[1.0625rem]">
                <VendorLogo className="md:mr-[1.6875rem]" alt="logo" src={logo} />
                <div className="mt-[13px] md:mt-0 self-center flex-1 flex flex-col">
                  <div className="flex md:flex-row flex-col">
                    <div className="flex flex-col flex-1 items-center md:items-start">
                      <SolutionTitle solution={solution} />
                      {/* {SHOW_REVIEWS && (
                        <ThemeOnly theme="legaltech">
                          <SolutionHeaderReviewStar
                            key={randomNum + 'SolutionHeaderReviewStar'}
                            reviews={
                              solution?.reviews?.filter(
                                (review): review is ISolutionReviewFragment => review !== null && review !== undefined,
                              ) || []
                            }
                          />
                        </ThemeOnly>
                      )} */}
                    </div>
                    <ThemeOnly theme="legaltech">
                      {query['viewType'] !== 'manage' && (
                        <Hidden mdDown>
                          <div className="flex flex-wrap gap-[11px]">{ActionButtons}</div>
                        </Hidden>
                      )}
                      {query['viewType'] === 'manage' &&
                        loginUserId &&
                        loginUserId != '' &&
                        solutionVendorUsers?.length > 0 &&
                        solutionVendorUsers?.includes(loginUserId) && (
                          <div className="flex gap-2 items-center">
                            {callToAction?.url && (
                              <CallToActionButton
                                key={randomNum + 'CallToActionButton'}
                                label={callToAction.label}
                                url={callToAction.url}
                                displayName={displayName}
                                solutionId={solution.documentId!}
                                solutionType={solutionType}
                              />
                            )}
                            <EditListingButton
                              key={randomNum + 'EditListingButton'}
                              slug={solution?.slug || ''}
                              solutionType={solutionType.toString().toLowerCase() || ''}
                            />
                          </div>
                        )}
                    </ThemeOnly>
                  </div>
                  {isProductSolution(solution) &&
                    checkNotEmpty(publishedDate) &&
                    (isGraveyard ? (
                      <div className="flex flex-row mb-1.5 h-[20px] w-fit bg-burlyWood rounded-[3px] items-center pl-[5px] pr-[5px]">
                        <Image src={icGraveyard} alt="" width={20} height={15} />
                        <p className="pl-[5px] h-[20px] text-textdark font-primary text-[15px] font-bold leading-[18px]">
                          {publishedDate}
                        </p>
                      </div>
                    ) : acquiredBy ? (
                      <p className="h-[20px] text-textdark font-primary text-[15px] font-bold leading-[18px]">
                        {publishedDate}
                      </p>
                    ) : (
                      ''
                    ))}
                  <Hidden mdDown>
                    <h2
                      className={cn('text-[1.125rem] font-primary font-regular text-darkgray', {
                        '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                      })}
                    >
                      <div className="value-proposition">
                        <HtmlContentViewer
                          html={solution?.shortDescription || ''}
                          contentClassName={cn({
                            '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                          })}
                        />
                      </div>
                    </h2>
                    {showClaimYourListing && <ClaimYourListing solution={solution} />}
                  </Hidden>
                </div>
              </div>
              <ThemeOnly theme="ilta">
                <Hidden mdDown>
                  <div className="flex flex-col ml-[50px] items-end">
                    <div className="flex flex-wrap gap-[11px]">{ActionButtons}</div>
                    {iltaSponsorshipLogo && (
                      <Image
                        src={iltaSponsorshipLogo}
                        width={200}
                        height={200}
                        className="w-[150px] h-auto mt-[30px]"
                        alt={''}
                      />
                    )}
                  </div>
                </Hidden>
              </ThemeOnly>
            </div>
            <Hidden mdUp>
              {query['viewType'] !== 'manage' && (
                <div className="flex flex-wrap w-full pt-[20px] md:pt-0 gap-[11px]">{ActionButtons}</div>
              )}
              <h2
                className={cn(
                  'text-[1.125rem] font-primary font-regular text-darkgray md:mt-[0rem] md:mb-[0rem] mt-[20px]',
                  { '!font-tertiary !font-regular text-[16px]': THEME === 'ilta' },
                )}
              >
                <HtmlContentViewer
                  html={solution?.shortDescription || ''}
                  contentClassName={cn({
                    '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                  })}
                />
              </h2>
              {showClaimYourListing && <ClaimYourListing solution={solution} />}
            </Hidden>
            <div className="w-full overflow-x-auto md:mt-[3.75rem] md:h-[3.125rem]">
              <div className="inline-flex gap-[40px]">
                <ProductPageTab
                  key={`${randomNum}-${DETAILS_TAB}`}
                  id={DETAILS_TAB}
                  activeTab={tabIdx}
                  className=""
                  onSelectTab={onSelectTab}
                >
                  Overview
                </ProductPageTab>
                {/* {SHOW_REVIEWS && (
                  <ThemeOnly theme="legaltech">
                    <ProductPageTab
                      key={`${randomNum}-${REVIEWS_TAB}`}
                      id={REVIEWS_TAB}
                      activeTab={tabIdx}
                      className=""
                      onSelectTab={onSelectTab}
                    >
                      Reviews
                    </ProductPageTab>
                  </ThemeOnly>
                )} */}
                {checkNotEmpty(caseStudies) && (
                  <ProductPageTab
                    key={`${randomNum}-${CASE_TAB}`}
                    id={CASE_TAB}
                    activeTab={tabIdx}
                    className=""
                    onSelectTab={onSelectTab}
                  >
                    Case Studies
                  </ProductPageTab>
                )}
                {checkNotEmpty(enhancedListingData?.pricingData) && (
                  <ProductPageTab
                    key={`${randomNum}-${PRICING_TAB}`}
                    id={PRICING_TAB}
                    activeTab={tabIdx}
                    className=""
                    onSelectTab={onSelectTab}
                  >
                    Pricing Model
                  </ProductPageTab>
                )}
                {(checkNotEmpty(enhancedListingData?.videos) || checkNotEmpty(enhancedListingData?.screenshots)) && (
                  <ProductPageTab
                    key={`${randomNum}-${VIDEOS_TAB}`}
                    id={VIDEOS_TAB}
                    activeTab={tabIdx}
                    className=""
                    onSelectTab={onSelectTab}
                  >
                    Videos & Screenshots
                  </ProductPageTab>
                )}

                {showProductBriefing && (
                  <ThemeOnly theme="legaltech">
                    <ProductPageTab
                      key={`${randomNum}-${PRODUCT_BRIEFING_TAB}`}
                      id={PRODUCT_BRIEFING_TAB}
                      activeTab={tabIdx}
                      className=""
                      onSelectTab={onSelectTab}
                    >
                      Product Briefing
                    </ProductPageTab>
                  </ThemeOnly>
                )}

                {/* {(!isVendor ||
                  (solutionVendorUsers?.length > 0 && solutionVendorUsers?.includes(session?.user.id || ''))) &&
                  session?.user.name &&
                  showVendorAnalysis && (
                    <ProductPageTab
                      key={`${randomNum}-${VENDOR_ANALYSIS_TAB}`}
                      id={VENDOR_ANALYSIS_TAB}
                      activeTab={tabIdx}
                      className=""
                      onSelectTab={onSelectTab}
                    >
                      Vendor Analysis
                    </ProductPageTab>
                  )} */}

                {/* {(!isVendor ||
                  (solutionVendorUsers?.length > 0 && solutionVendorUsers?.includes(session?.user.id || ''))) &&
                  showVendorAnalysis && (
                    <ProductPageTab
                      key={`${randomNum}-${VENDOR_ANALYSIS_TAB}`}
                      id={VENDOR_ANALYSIS_TAB}
                      activeTab={tabIdx}
                      className=""
                      onSelectTab={onSelectTab}
                    >
                      Vendor Analysis
                    </ProductPageTab>
                  )} */}
                {enableVendorAnalysisTab && (
                  <ThemeOnly theme="legaltech">
                    <ProductPageTab
                      key={`${randomNum}-${VENDOR_ANALYSIS_TAB}`}
                      id={VENDOR_ANALYSIS_TAB}
                      activeTab={tabIdx}
                      className=""
                      onSelectTab={onSelectTab}
                    >
                      Vendor Analysis
                    </ProductPageTab>
                  </ThemeOnly>
                )}
                {checkNotEmpty(solution?.vendorResources) && showThoughtLeadershipTab && (
                  <ProductPageTab
                    key={`${randomNum}-${RESOURCE_TAB}`}
                    id={RESOURCE_TAB}
                    activeTab={tabIdx}
                    className=""
                    onSelectTab={onSelectTab}
                  >
                    Thought Leadership
                  </ProductPageTab>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
)
